import { useFeatureFlagsContext } from '../../../FeatureFlags';
import { useClientContext } from '../../../ClientContext';
import { getIsBeachMode, ScorecardElementSymbol, useScorecard, useVisibleScorecardElements } from '../../../Scorecard';
import { useTargetScoreRangesForElements } from '../../hooks';
import { ScoreTileWrapper, ScoreTileWrapperElementSlot } from './ScoreTileWrapper';
import { useBtDetails } from '../../../BtDetails';
import { clientHasBusinesses } from '../../../FirmClient';
import { getVisibleScorecardGridRows } from './scorecardVisibility';

export type ElementRoutes = {
  Bt: string;
  Lt: string;
  Qt: string;
  Rt: string;
  Tt: string;
  Sr: string;
  Dr: string;
  Tr: string;
  Br: string;
  Er: string;
  Ir: string;
  Es?: string;
};

export function ScorecardGrid(props: {
  elementSlot: ScoreTileWrapperElementSlot;
  elementSlot1: ScoreTileWrapperElementSlot;
  elementSlotEs?: ScoreTileWrapperElementSlot;
  elementSlot2: ScoreTileWrapperElementSlot;
  elementSlot3: ScoreTileWrapperElementSlot;
  elementSlot4: ScoreTileWrapperElementSlot;
  elementSlot5: ScoreTileWrapperElementSlot;
  elementSlot6: ScoreTileWrapperElementSlot;
  elementSlot7: ScoreTileWrapperElementSlot;
  elementSlot8: ScoreTileWrapperElementSlot;
  elementSlot9: ScoreTileWrapperElementSlot;
  elementSlot10: ScoreTileWrapperElementSlot;
  Routes: ElementRoutes;
  forceShowSkeleton?: boolean;
  visibleElements?: ScorecardElementSymbol[];
}) {
  const { allowEstateStatus, allowTargetScoreRanges } = useFeatureFlagsContext();
  const { householdID, clientID, firmID } = useClientContext();
  const { scorecard, scorecardQuery } = useScorecard({ householdID });
  const { btDetails } = useBtDetails({ householdID, includeBusinesses: true, includePrivateStock: true });
  const { ir, er, es, sr, br, dr, tr, lt, qt, rt, bt, tt } = useTargetScoreRangesForElements({ firmID, clientID });
  const beachMode = getIsBeachMode(scorecard);
  const btIsNull = !clientHasBusinesses(btDetails?.businesses, btDetails?.privateStock);
  const showEstateStatus = allowEstateStatus === true && props.Routes.Es && props.elementSlotEs;
  const visibleElements = useVisibleScorecardElements(props.visibleElements).filter(
    (symbol) => symbol !== 'Es' || showEstateStatus
  );
  const rowSymbols = getVisibleScorecardGridRows(visibleElements);
  const elementSlots: Partial<Record<ScorecardElementSymbol, ScoreTileWrapperElementSlot>> = {
    Er: props.elementSlot,
    Ir: props.elementSlot1,
    Es: props.elementSlotEs,
    Sr: props.elementSlot2,
    Br: props.elementSlot3,
    Dr: props.elementSlot4,
    Tr: props.elementSlot5,
    Lt: props.elementSlot6,
    Qt: props.elementSlot7,
    Rt: props.elementSlot8,
    Bt: props.elementSlot9,
    Tt: props.elementSlot10,
  };
  const targets = { Er: er, Ir: ir, Es: es, Sr: sr, Br: br, Dr: dr, Tr: tr, Lt: lt, Qt: qt, Rt: rt, Bt: bt, Tt: tt };
  const scores = {
    Er: scorecard?.er,
    Ir: scorecard?.ir,
    Es: scorecard?.es,
    Sr: scorecard?.sr,
    Br: scorecard?.br,
    Dr: scorecard?.dr,
    Tr: scorecard?.tr,
    Lt: scorecard?.lt,
    Qt: scorecard?.qt,
    Rt: scorecard?.rt,
    Bt: scorecard?.bt,
    Tt: scorecard?.tt,
  };
  const skeletonClasses = {
    Er: 'bg-elements-Er/20',
    Ir: 'bg-elements-Ir/20',
    Es: 'bg-elements-Es/20',
    Sr: 'bg-elements-Sr/20',
    Br: 'bg-elements-Br/20',
    Dr: 'bg-elements-Dr/20',
    Tr: 'bg-elements-Tr/20',
    Lt: 'bg-elements-Lt/20',
    Qt: 'bg-elements-Qt/20',
    Rt: 'bg-elements-Rt/20',
    Bt: 'bg-elements-Bt/20',
    Tt: 'bg-elements-Tt/20',
  } as const;

  const showSkeleton = !scorecard || scorecardQuery.isLoading || props.forceShowSkeleton;

  return (
    <div className={'flex flex-col justify-start items-start gap-1.5 sm:gap-4'}>
      {rowSymbols.map((row) => (
        <div key={row.join('-')} className={'flex flex-row justify-start gap-1.5 sm:gap-4 flex-nowrap'}>
          {row.map((symbol) => {
            const elementSlot = elementSlots[symbol];
            const navigateTo = props.Routes[symbol];
            if (!elementSlot || !navigateTo) return null;

            return showSkeleton ? (
              <div
                key={symbol}
                className={`animate-pulse ${skeletonClasses[symbol]} w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none`}
              />
            ) : (
              <ScoreTileWrapper
                key={symbol}
                scorecard={scorecard}
                elementSymbol={symbol}
                showRange={allowTargetScoreRanges && (symbol !== 'Bt' || !btIsNull)}
                score={scores[symbol]}
                elementSlot={elementSlot}
                navigateTo={navigateTo}
                min={targets[symbol].min}
                max={targets[symbol].max}
                beachMode={
                  symbol === 'Lt' || symbol === 'Qt' || symbol === 'Rt' || symbol === 'Bt' || symbol === 'Tt'
                    ? beachMode
                    : false
                }
                btIsNull={symbol === 'Bt' ? btIsNull : false}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
